/**
 * Home's default copy (the rule is in `shared.ts`): the hero, the banner carousel's name, the promo
 * cards, the FAQ and the row of boxes.
 */
import type { StorefrontFeatureKey } from "../index.js";
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { browseBoxesLabel, type CopyRun } from "./shared.js";

/** Home's hero, shown while no banner is live: the headline, the body and its actions' labels. */
export interface HeroCopy {
  headline: string;
  body: string;
  signUp: string;
  browse: string;
}

export function defaultHeroCopy(terms: StorefrontTerminology): HeroCopy {
  const { box, item } = terms;
  return {
    headline: "Rip. Collect. Hold.",
    body: `Open digital ${box.plural}, discover rare ${item.plural}, and build your collection. Love what you opened? Get the real thing delivered straight to your door.`,
    signUp: "Sign up",
    browse: browseBoxesLabel(terms),
  };
}

/** What Home's banner carousel is called, read with it by screen readers. */
export const HOME_BANNERS_LABEL = "Featured";

/** Home's promo row holds at most this many cards. */
export const PROMO_CARD_LIMIT = 3;

/** One of Home's promo cards: a card-wide link to a part of the store. */
export interface PromoCardCopy {
  key: "boxes" | "battles" | "forge" | "fairness";
  eyebrow: string;
  title: string;
  href: "/boxes" | "/battles" | "/forge" | "/fairness";
}

/**
 * Home's promo cards: the games the store has on (the boxes, then battles and the forge), then
 * the fairness page, up to `PROMO_CARD_LIMIT`. Sign-up lives in the hero and the header, so no
 * card repeats it. No free-pack offer: the design has one, but nothing in loot-os grants it.
 */
export function defaultPromoCards(
  terms: StorefrontTerminology,
  hasFeature: (key: StorefrontFeatureKey) => boolean,
): PromoCardCopy[] {
  const { box, battle, forge } = terms;
  const cards: PromoCardCopy[] = [
    {
      key: "boxes",
      eyebrow: `Every ${box.singular}'s odds, published`,
      title: `Rip ${box.plural}`,
      href: "/boxes",
    },
  ];
  if (hasFeature("battles")) {
    cards.push({
      key: "battles",
      eyebrow: "Best pull takes the lot",
      title: capitalizeTerm(battle.plural),
      href: "/battles",
    });
  }
  if (hasFeature("forge")) {
    cards.push({
      key: "forge",
      eyebrow: `Make ${box.plural} of your own`,
      title: capitalizeTerm(forge.singular),
      href: "/forge",
    });
  }
  cards.push({
    key: "fairness",
    eyebrow: "Verify every open",
    title: "Provably fair",
    href: "/fairness",
  });
  return cards.slice(0, PROMO_CARD_LIMIT);
}

export interface FaqCopy {
  title: string;
  entries: { question: string; answer: CopyRun[] }[];
}

/**
 * Home's FAQ. Delivery is worded as arranged through the store: withdrawing is a secret-key route
 * the tenant's own server calls, never something the storefront offers.
 */
export function defaultFaq({
  terms,
  storeName,
  currencyPlural,
}: {
  terms: StorefrontTerminology;
  storeName: string;
  /** The currency's plural name, or its name when it has none. */
  currencyPlural: string;
}): FaqCopy {
  const { box, item, inventory } = terms;
  return {
    title: "Frequently Asked Questions",
    entries: [
      {
        question: `How does opening ${box.plural} work?`,
        answer: [
          `Every ${box.singular} lists its full odds before you open it. When you open one, the ${item.plural} are revealed straight away and held against your balance; claiming them is what completes the purchase and moves them into your ${inventory.singular}.`,
        ],
      },
      {
        question: "How do I know the odds are real?",
        answer: [
          "Every open is provably fair: the result is fixed by a server seed published in advance and your own client seed, so neither side can change it after the fact. You can check any open yourself on the ",
          { text: "fairness page", href: "/fairness" },
          ".",
        ],
      },
      {
        question: "What can I do with what I open?",
        answer: [
          `Keep it in your ${inventory.singular}, or sell it back to ${storeName} for its listed value — in full, with no fee. Selling back is all or nothing for the ${item.plural} you pick.`,
        ],
      },
      {
        question: `Can I get the physical ${item.singular}?`,
        answer: [
          `Yes. ${capitalizeTerm(item.plural)} you have claimed can be sent out as real goods, arranged through ${storeName}, with shipping charged for the destination you choose.`,
        ],
      },
      {
        question: `What are ${currencyPlural}?`,
        answer: [
          `${currencyPlural} are the store's balance. Prices, your balance and anything you sell back are all shown in ${currencyPlural}.`,
        ],
      },
    ],
  };
}

/** Home's row of boxes: its heading and the link to the whole catalog. */
export function defaultFeaturedBoxesCopy(terms: StorefrontTerminology): {
  title: string;
  viewAll: string;
} {
  return {
    title: capitalizeTerm(terms.box.plural),
    viewAll: `View all ${terms.box.plural}`,
  };
}

/** How many battles Home shows: open ones, then running, then the latest finished. */
export const HOME_BATTLES_LIMIT = 4;

/** Home's battles: its heading and the link to the lobby. */
export function defaultHomeBattlesCopy(terms: StorefrontTerminology): {
  title: string;
  viewAll: string;
} {
  return {
    title: capitalizeTerm(terms.battle.plural),
    viewAll: `View all ${terms.battle.plural}`,
  };
}

/**
 * Home's pitch for the Forge: build a box, open it or share it, and, where the store pays
 * commission, earn up to its most from every open (`maxCutPercent`, 0 when it pays none).
 */
export function defaultHomeForgeCopy(
  terms: StorefrontTerminology,
  maxCutPercent: number,
): { title: string; body: string; create: string; browse: string } {
  const { box, item } = terms;
  return {
    title: `Create your own ${box.singular}`,
    body:
      maxCutPercent > 0
        ? `Pick the ${item.plural} that go in it, open it yourself, or share it and earn up to ${maxCutPercent}% of every opening.`
        : `Pick the ${item.plural} that go in it, then open it yourself or share it.`,
    create: "Create",
    browse: "Browse",
  };
}
